<script lang="ts" setup>
import type { DateTime } from 'luxon';
import type { AutocompleteListModel as ProductListModel } from '../../../pages/store/catalog/products/_runtime/services/ProductsService';
import ProductsService from '../../../pages/store/catalog/products/_runtime/services/ProductsService';

defineOptions({
    name: 'WidgetSelectedProductsSettings'
});

interface FormSettings
{
    settings: {
        datePublishedFromUtc?: DateTime;
        datePublishedDueUtc?: DateTime;
        format: string;
        productsIds: number[];
    };
}

const model = defineModel<FormType<FormSettings>>();
const defaults: FormSettings['settings'] = {
    format: 'slider',
    productsIds: []
};

const form = computed(() => model.value);

onMounted(async () =>
{
    form.value.settings = { ...defaults, ...form.value.settings };
});

const productId = ref<number>(null);
const products = computedAsync(async () =>
{
    try
    {
        if (form.value.settings.productsIds.length === 0)
            return [];

        return await ProductsService.getListByIds(form.value.settings.productsIds);
    }
    catch (ex)
    {
        return [];
    }
});

const formatOptions: Option[] = [
    { value: 'slider', text: $t('[[[slider]]]') },
    { value: 'grid', text: $t('[[[grid]]]') }
];

const fetchProduct = async (id: number): Promise<ProductListModel> =>
{
    const response = await ProductsService.fetchAutocompleteModel(id);

    return response.first() ?? null;
};

const searchProduct = async (query: string, limit: number): Promise<ProductListModel[]> =>
{
    return await ProductsService.getAutocompleteList(query, limit, form.value.settings.productsIds);
};

const addProduct = async (): Promise<void> =>
{
    if (productId.value == null)
        return;

    form.value.settings.productsIds.push(productId.value);
    productId.value = null;
};

const removeProduct = async (id: number): Promise<void> =>
{
    form.value.settings.productsIds = form.value.settings.productsIds.filter(p => p !== id);
};

async function validate()
{
    form.value.validate(schema => ({
        settings: schema.object({
            datePublishedFromUtc: schema.date().required($t('[[[Data rozpoczęcia publikacji jest wymagana]]]')).typeError($t('[[[Data rozpoczęcia publikacji jest wymagana]]]')),
            format: schema.string().required($t('[[[Wybranie formatu jest wymagane]]]')).typeError($t('[[[Wybranie formatu jest wymagane]]]')),
            productsIds: schema.array(schema.number().required($t('[[[Wybranie produktu jest wymagane]]]'))).min(1, $t('[[[Wybranie produktu jest wymagane]]]')).typeError($t('[[[Wybranie produktu jest wymagane]]]'))
        })
    }));
}

watch(() => form.value.settings.datePublishedFromUtc, () =>
{
    form.value.$errors.clear('settings.datePublishedFromUtc');
});

watch(() => form.value.settings.format, () =>
{
    form.value.$errors.clear('settings.format');
});

watch(() => form.value.settings.productsIds, () =>
{
    form.value.$errors.clear('settings.productsIds');
}, { deep: true });

defineExpose({
    validate
});
</script>

<template>
    <IdeoFormGroup>
        <div class="row">
            <div class="col-md-6">
                <IdeoFormGroup :label="$t('[[[Publikuj od]]]')" :invalid-feedback="form.$errors.first('settings.datePublishedFromUtc')" :state="form.$errors.state('settings.datePublishedFromUtc')" required>
                    <IdeoDatetime v-model="form.settings.datePublishedFromUtc" name="datePublishedFromUtc" type="datetime" />
                </IdeoFormGroup>
            </div>
            <div class="col-md-6">
                <IdeoFormGroup :label="$t('[[[Publikuj do]]]')" :invalid-feedback="form.$errors.first('settings.datePublishedDueUtc')" :state="form.$errors.state('settings.datePublishedDueUtc')">
                    <IdeoDatetime v-model="form.settings.datePublishedDueUtc" name="datePublishedDueUtc" type="datetime" />
                </IdeoFormGroup>
            </div>
        </div>

        <IdeoFormGroup :label="$t('[[[Szablon]]]')" :invalid-feedback="form.$errors.first('settings.format')" :state="form.$errors.state('settings.format')">
            <IdeoFormSelect v-model="form.settings.format" :options="formatOptions" />
        </IdeoFormGroup>

        <IdeoFormGroup :label="$t('[[[Wybierz produkty]]]')" :invalid-feedback="form.$errors.first('settings.productsIds')" :state="form.$errors.state('settings.productsIds')">
            <div class="d-flex flex-column flex-md-row">
                <div class="flex-grow-1">
                    <IdeoAutocomplete
                        v-model="productId"
                        name="productsIds"
                        track-by="id"
                        :placeholder="$t('[[[zacznij pisać aby wyszukać etykietę...]]]')"
                        :fetch="(id: number) => fetchProduct(id)"
                        :search="(query: string, limit: number) => searchProduct(query, limit)"
                        :refresh="true"
                    >
                        <template #selected="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                        <template #option="{ option }">
                            <div class="text-wrap">
                                {{ option.name }}
                            </div>
                        </template>
                    </IdeoAutocomplete>
                </div>
                <div class="d-flex align-items-stretch">
                    <button class="btn btn-secondary text-nowrap mt-2 mt-md-0 ms-md-2" @click.prevent="addProduct()">
                        <i class="fas fa-plus" />
                    </button>
                </div>
            </div>
        </IdeoFormGroup>

        <h5 class="mb-3">
            {{ $t('[[[Produkty]]]') }}
        </h5>
        <IdeoFormGroup v-if="products">
            <div class="list-group">
                <div v-for="item in products" :key="item.id" class="list-group-item list-group-item-action p-2 d-flex">
                    <div class="d-flex align-items-center justify-content-between gap-2 w-100">
                        <div class="d-flex align-items-center gap-2">
                            <i class="fad fa-fw fa-browser text-primary" />
                            <b :title="$t('[[[Nazwa]]]')">
                                {{ item.name }}
                            </b>
                        </div>

                        <div class="d-flex align-items-center">
                            <Confirmation :message="$t('[[[Potwierdzenie usunięcia]]]')" :target="`btn-delete-product-${item.id}`" :value="item.id" @confirm="removeProduct(item.id)" />
                            <IdeoButton :id="`btn-delete-product-${item.id}`" variant="danger" size="sm" pill :title="$t('[[[Usuń produkt]]]')">
                                <i class="fas fa-trash-alt" />
                            </IdeoButton>
                        </div>
                    </div>
                </div>
                <div v-if="form.settings.productsIds.length == 0" class="list-group-item list-group-item-light text-center">
                    {{ $t('[[[Brak wybranych produktów]]]') }}
                </div>
            </div>
        </IdeoFormGroup>
    </IdeoFormGroup>
</template>
